import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useCookies } from '../hooks/useCookies';
import { updateOnlineStatus as updateOnlineStatusAction } from '../actions/onlineStatus';

const OnlineStatusContext = createContext();

export const useOnlineStatus = () => {
    const context = useContext(OnlineStatusContext);
    if (!context) {
        throw new Error('useOnlineStatus must be used within an OnlineStatusProvider');
    }
    return context;
};

export const OnlineStatusProvider = ({ children }) => {
    const { getCookie } = useCookies();
    const [isOnline, setIsOnline] = useState(true);
    const [lastSeen, setLastSeen] = useState(null);
    const [isPageVisible, setIsPageVisible] = useState(true);
    const heartbeatIntervalRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const initialStatusSetRef = useRef(false);
    const isUpdatingRef = useRef(false);

    // Update user's online status on the server
    const updateOnlineStatus = useCallback(async (status) => {
        const token = getCookie('token');
        if (!token) return;

        // Prevent multiple simultaneous calls
        if (isUpdatingRef.current) return;
        isUpdatingRef.current = true;

        try {
            const result = await updateOnlineStatusAction(token, status);

            if (result.success) {
                setLastSeen(result.lastSeen);
                lastActivityRef.current = result.timestamp;
            }
        } catch (error) {
            console.error('Failed to update online status:', error);
        } finally {
            isUpdatingRef.current = false;
        }
    }, [getCookie]);

    // Handle page visibility changes
    const handleVisibilityChange = useCallback(() => {
        const visible = !document.hidden;
        setIsPageVisible(visible);

        if (visible) {
            // User came back to the page
            setIsOnline(true);
            updateOnlineStatus(true);
        } else {
            // User left the page
            setIsOnline(false);
            updateOnlineStatus(false);
        }
    }, [updateOnlineStatus]);

    // Handle beforeunload event (when user closes tab/browser)
    const handleBeforeUnload = useCallback(() => {
        updateOnlineStatus(false);
    }, [updateOnlineStatus]);

    // Handle online/offline network status
    const handleOnlineStatusChange = useCallback(() => {
        const online = navigator.onLine;
        setIsOnline(online);

        if (online) {
            updateOnlineStatus(true);
        } else {
            updateOnlineStatus(false);
        }
    }, [updateOnlineStatus]);

    // Initialize online status and set up event listeners
    useEffect(() => {
        const token = getCookie('token');

        // Clear any existing interval
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }

        // Reset initial status flag when token changes
        initialStatusSetRef.current = false;

        // Only proceed if we have a token
        if (!token) {
            return;
        }

        // Set initial online status only once
        if (!initialStatusSetRef.current) {
            updateOnlineStatus(true);
            initialStatusSetRef.current = true;
        }

        // Start heartbeat interval (every 30 seconds)
        heartbeatIntervalRef.current = setInterval(() => {
            updateOnlineStatus(true);
        }, 30000); // 30 seconds = 30000ms

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('online', handleOnlineStatusChange);
        window.addEventListener('offline', handleOnlineStatusChange);

        // Cleanup function
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('online', handleOnlineStatusChange);
            window.removeEventListener('offline', handleOnlineStatusChange);

            // Set offline status when component unmounts (only if we have a token)
            if (token) {
                updateOnlineStatus(false);
            }
        };
    }, [getCookie, updateOnlineStatus, handleVisibilityChange, handleBeforeUnload, handleOnlineStatusChange]); // Add all dependencies

    const value = {
        isOnline,
        lastSeen,
        isPageVisible,
        updateOnlineStatus,
    };

    return (
        <OnlineStatusContext.Provider value={value}>
            {children}
        </OnlineStatusContext.Provider>
    );
};
